import * as React from "react";
import { Button, Card, Form, Image } from "react-bootstrap";
import { ActionHandlerFunction, WebAuthnKeyRegister } from "@preveil-api";
import { AuthenticationTypes, CreateAccountSteps, CreateAccountUIActionTypes, getWebAuthErrorMessage, Message, MessageHandlerDisplayType, MessageToastTypes, registerWebAuthnKey, SettingsMessages, useAppDispatch } from "src/common";
import { InfoPopover } from "src/components";
import { uiActions } from "src/store";
import smsImage from "src/assets/images/account/2fa/sms-image.svg";
import totpImage from "src/assets/images/account/2fa/totp-image.svg";
import webAuthKeyImage from "src/assets/images/account/2fa/webauthkey-image.svg";

type AllProps = {
  handleAction: ActionHandlerFunction;
  user_id: string;
  display_name: string;
}

// Description: Component for selecting a two-factor login method during account creation
function Pick2FAFormComponent(props: AllProps) {
  const { handleAction, user_id, display_name } = props;
  const dispatch = useAppDispatch();
  const [selected, setSelected] = React.useState<string>("");

  // Description: Handle the change of two-factor type - SMS, TOTP, or WebAuthn. If WebAuthn, register a new key and send it to the server
  function handleTwoFactorTypeChange(type: string) {
    setSelected(type);
    const params = type === AuthenticationTypes.SMS ? CreateAccountSteps.SUBMIT_PHONE : (type === AuthenticationTypes.TOTP) ? CreateAccountSteps.SUBMIT_TOTP_CODE : undefined;
    if (!!params) {
      handleAction({ actionType: CreateAccountUIActionTypes.ChangeStep, params });
    } else {
      registerWebAuthnKey(user_id, display_name).then((web_authn_key: WebAuthnKeyRegister | undefined) => {
        if (!!web_authn_key) {
          const decoded_context = JSON.parse(atob(web_authn_key.context));
          handleAction({ actionType: CreateAccountUIActionTypes.SubmitWebAuthnKey, params: { web_authn_key, type: decoded_context.type } });
        } else {
          setSelected("");
          handleError("Error registering WebAuthn key", web_authn_key);
        }
      }).catch((error: any) => {
        setSelected("");
        if (error.name === "NotAllowedError" || error.name === "AbortError" || error.name === "InvalidStateError") {
          dispatch(uiActions.handleSetMessage(new Message(getWebAuthErrorMessage(error), MessageHandlerDisplayType.toaster, MessageToastTypes.warning)));
        } else {
          handleError(getWebAuthErrorMessage(error), error);
        }
      });
    }
  }

  // Description: Send error up and reset form
  function handleError(message: string, stack?: any) {
    handleAction({
      actionType: CreateAccountUIActionTypes.PageError,
      params: { message, stack }
    });
  };

  return <Card.Body>
    <h4>Select a two-factor login method</h4>
    <Form className="two-factor-type">
      <div>
        <Form.Check
          className="radio-button"
          onChange={() => handleTwoFactorTypeChange(AuthenticationTypes.SMS)}
          name="two_factor_type"
          checked={selected === AuthenticationTypes.SMS}
          type={"radio"}
        />
        <Image src={smsImage} />
        <div className="text-container">
          <p className="semi-bold">Text Message</p>
          <p>You will receive a one-time passcode</p>
        </div>
      </div>
      <div>
        <Form.Check
          className="radio-button"
          onChange={() => handleTwoFactorTypeChange(AuthenticationTypes.TOTP)}
          name="two_factor_type"
          checked={selected === AuthenticationTypes.TOTP}
          type={"radio"}
        />
        <Image src={totpImage} />
        <div className="text-container">
          <p className="semi-bold">Authenticator Application</p>
          <p>Set up your favorite authenticator app to log in to PreVeil</p>
        </div>
      </div>
      <div>
        <Form.Check
          className="radio-button"
          onChange={() => handleTwoFactorTypeChange(AuthenticationTypes.WEBAUTHN_CREDENTIAL)}
          name="two_factor_type"
          type={"radio"}
          checked={selected === AuthenticationTypes.WEBAUTHN_CREDENTIAL}
        />
        <Image src={webAuthKeyImage} />
        <div className="text-container">
          <p className="semi-bold">Passkey or Security Key <InfoPopover message={SettingsMessages.web_auth_tooltip} placement="top"/></p>
          <p>Set up a FedRAMP compliant secure key type to log into PreVeil</p>
        </div>
      </div>
    </Form>
    
    <div className="btn-panel">
      <Button variant="outline-secondary" type="button"
        onClick={() => handleAction({ actionType: CreateAccountUIActionTypes.ResetForms })}>Cancel</Button>
    </div>
  </Card.Body>;
}

export default React.memo(Pick2FAFormComponent);
